import * as React from 'react';
import Form from 'antd/lib/form';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { Radio } from 'antd/lib';
import SupportedTokensComponent from '../../common/SupportedTokensComponent';
import { IntegrationType } from '@/types';
import type { Nullable } from '@/typescriptHelpers';
import { TestRunsFilterSettings } from './TestRunsFilterSettings';

type TestPlansFilterSettingsProps = {
    rootFieldName: string | number;
    integrationType: Nullable<IntegrationType>;
};

export const TestPlansFilterSettings = ({ rootFieldName, integrationType }: TestPlansFilterSettingsProps) => {
    const hasSupportForCustomDuration = integrationType === IntegrationType.BrowserStack;

    return (
        <Form.List name={rootFieldName}>
            {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {fields.map((field, index) => {
                        return (
                            <div
                                key={field.key}
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: 20,
                                    borderRadius: 8,
                                    marginBottom: 16,
                                    border: `1px solid ${antGrey.grey5}`,
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                }}
                            >
                                <Text strong style={{ paddingBottom: 2 }}>
                                    Test plan - pattern {index + 1}
                                </Text>
                                {index > 0 && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            paddingRight: 14,
                                            paddingTop: 8,
                                        }}
                                    >
                                        <Button
                                            icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                            danger
                                            type="link"
                                            onClick={() => remove(index)}
                                        />
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0,
                                        marginTop: 6,
                                    }}
                                >
                                    <Form.Item
                                        name={[field.name, 'pattern']}
                                        rules={[{ required: true, message: 'Please add a pattern' }]}
                                        extra={<SupportedTokensComponent />}
                                        style={{ marginBottom: 16 }}
                                    >
                                        <Input
                                            allowClear
                                            style={{ width: '100%' }}
                                            placeholder="{appPlatform} {version} test plan"
                                        />
                                    </Form.Item>

                                    {hasSupportForCustomDuration && (
                                        <Form.Item
                                            label="Duration in days"
                                            name={[field.name, 'durationInDays']}
                                            rules={[
                                                {
                                                    type: 'number',
                                                    min: 1,
                                                    message: 'Duration must be a positive integer',
                                                },
                                            ]}
                                            tooltip="The creation date will be considered the start date. For example, setting 7 days will create a test plan from today through 6 days from now."
                                        >
                                            <InputNumber
                                                placeholder="Number of days (Optional)"
                                                style={{ width: '100%' }}
                                                min={1}
                                                max={100}
                                                precision={0}
                                            />
                                        </Form.Item>
                                    )}

                                    <Form.Item
                                        initialValue={true}
                                        rules={[{ required: true, message: 'Please select an option' }]}
                                        name={[field.name, 'shouldMatchAllUniqueTestRunsPerPlan']}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Radio.Group>
                                            <Radio value={true}>Match all unique test runs per plan</Radio>
                                            <Radio value={false}>Specify test run patterns</Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item
                                        noStyle
                                        // Only update when shouldMatchAllUniqueTestRunsPerPlan changes
                                        shouldUpdate={(previousForm: any, currentForm: any) => {
                                            const previousValue =
                                                previousForm[rootFieldName]?.[field.name]
                                                    ?.shouldMatchAllUniqueTestRunsPerPlan;
                                            const currentValue =
                                                currentForm[rootFieldName]?.[field.name]
                                                    ?.shouldMatchAllUniqueTestRunsPerPlan;
                                            return previousValue !== currentValue;
                                        }}
                                    >
                                        {(form) => {
                                            const shouldMatchAllUniqueTestRunsPerPlan = form.getFieldValue([
                                                rootFieldName,
                                                field.name,
                                                'shouldMatchAllUniqueTestRunsPerPlan',
                                            ]);

                                            if (shouldMatchAllUniqueTestRunsPerPlan) {
                                                return null;
                                            }

                                            return (
                                                <div style={{ marginTop: 16 }}>
                                                    <TestRunsFilterSettings
                                                        rootFieldName={rootFieldName}
                                                        parentPath={[field.name, 'testRunFilters']}
                                                        integrationType={integrationType}
                                                    />
                                                </div>
                                            );
                                        }}
                                    </Form.Item>
                                </div>
                            </div>
                        );
                    })}
                    <Button icon={<PlusOutlined style={{ color: runwayColors.primary }} />} onClick={() => add()}>
                        Add another test plan pattern
                    </Button>
                </Space>
            )}
        </Form.List>
    );
};
